import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Email, Phone, Help, Book } from '@material-ui/icons';
import { SEND_EMAIL_URL } from '../../api/urls';
import { toast } from 'react-toastify';
import Axios from 'axios';

const HelpAndSupport = () => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    attachment: null as File | null,
  });
  const [emailStatus, setEmailStatus] = useState<string | null>(null);

  const categories = [
    { id: 'faq', name: 'Frequently Asked Questions', icon: <Help /> },
    { id: 'contact', name: 'Contact Support', icon: <Email /> },
    { id: 'docs', name: 'Documentation', icon: <Book /> },
  ];

  const accessToken = localStorage.getItem("access_token");

  const faqData = [
    {
      category: "General Questions",
      items: [
        { question: "What is the Balanced Scorecard (BSC) system?", answer: "The BSC system is a performance management tool that helps organizations track KPIs, manage strategic objectives, and measure performance using scorecards." },
        { question: "Who can access the BSC system?", answer: "Access is granted to authorized users, including managers, employees, and administrators based on role-based permissions." },
        { question: "How do I get started with the BSC system?", answer: "New users can refer to the documentation or contact support for an onboarding session." },
        { question: "How can I view my scorecard?", answer: "You can view your scorecard by navigating to the 'Scorecard' section on the dashboard. Here, you will see detailed metrics and KPIs related to your performance." }
      ]
    },
    {
      category: "Account & Security",
      items: [
        { question: "How do I change my password?", answer: "You can change your password from the 'Settings' section under 'Account Management.'" },
        { question: "What do I do if I forget my password?", answer: "Click on 'Forgot Password' on the login page and follow the instructions to reset your password." },
        { question: "Can I enable two-factor authentication (2FA) for my account?", answer: "Currently, the system does not support 2FA, but security measures such as session management and token-based authentication are in place." },
        { question: "How do I reset my password?", answer: "You can reset your password by clicking on the 'Forgot Password' link on the login page." },
        { question: "How do I update my personal information?", answer: "You can update your personal information by going to the 'Settings' page and editing your profile details. Ensure that your email and contact information are up to date." }
      ]
    },
    {
      category: "Technical Support",
      items: [
        { question: "What should I do if I encounter an error?", answer: "Try refreshing the page or clearing your browser cache. If the issue persists, contact support." },
        { question: "How do I report a bug?", answer: "You can report bugs through the 'Contact Support' section, providing as much detail as possible." },
        { question: "What should I do if I encounter a performance issue with the platform?", answer: "If you encounter performance issues, please contact the support team with details about the issue, including any error messages and the actions you were performing." },
        { question: "What do I do if my scorecard is not updating?", answer: "If your scorecard is not updating, try refreshing the page. If the issue persists, check if there are any pending approvals or tasks related to your scorecard. Contact support if further assistance is needed." }
      ]
    },
    {
      category: "Measures & Submissions",
      items: [
        { question: "How do I submit a measure for approval?", answer: "To submit a measure for approval, go to the 'Measures' section, select the measure you want to submit, and click 'Submit for Approval'. An administrator or manager will then review your submission." },
        { question: "How do I handle mandatory comments or evidence files?", answer: "When submitting a measure, ensure that you provide the required comments or attach any necessary evidence files. These are mandatory for the submission process." }
      ]
    },
    {
      category: "Dashboard & Filters",
      items: [
        { question: "How do I enable date filtering in the dashboard?", answer: "You can enable date filtering by using the date filters available at the top of the dashboard. Select the desired date range to filter the displayed KPIs and metrics accordingly." }
      ]
    }
  ];

  useEffect(() => {
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 1000);
  }, [selectedCategory]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFormData((prevData) => ({
        ...prevData,
        attachment: e.target.files ? e.target.files[0] : null,
      }));
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('sender', formData.email);
      formDataToSend.append('recipient', 'open@lites.co.ke');
      formDataToSend.append('name', formData.name);
      formDataToSend.append('message', formData.message);

      if (formData.attachment) {
        formDataToSend.append('attachment', formData.attachment);
      }

      const response = await Axios.post(SEND_EMAIL_URL, formDataToSend, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.status === 200) {
        setEmailStatus('Email sent successfully!');
        toast.success('Email sent successfully!');
        setFormData({ name: '', email: '', message: '', attachment: null });
      } else {
        throw new Error('Failed to send email');
      }
    } catch (error) {
      setEmailStatus('An error occurred. Please try again later.');
      toast.error('An error occurred. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredFAQ = faqData.map((cat) => ({
    category: cat.category,
    items: cat.items.filter((item) =>
      item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.answer.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(cat => cat.items.length > 0);

  return (
    <Container>
      <Content>
        <Sidebar>
          {categories.map((category) => (
            <CategoryButton
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              isActive={selectedCategory === category.id}
            >
              {category.icon} {category.name}
            </CategoryButton>
          ))}
        </Sidebar>
        <MainContent>
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <>
              {selectedCategory === 'faq' && (
                <FAQSection>
                  <SearchBar
                    type="text"
                    placeholder="Search FAQs..."
                    value={searchQuery}
                    onChange={handleSearch}
                  />
                  {filteredFAQ.map((category, index) => (
                    <FAQCategory key={index}>
                      <FAQCategoryTitle>{category.category}</FAQCategoryTitle>
                      {category.items.map((item, idx) => (
                        <FAQItem key={idx}>
                          <FAQQuestion>{item.question}</FAQQuestion>
                          <FAQAnswer>{item.answer}</FAQAnswer>
                        </FAQItem>
                      ))}
                    </FAQCategory>
                  ))}
                </FAQSection>
              )}
              {selectedCategory === 'contact' && (
                <ContactSection>
                  <ContactMethod>
                    <Email /> Email: open@lites.co.ke
                  </ContactMethod>
                  <ContactMethod>
                    <Phone /> Phone: +254 742 329976
                  </ContactMethod>
                  <ContactForm onSubmit={handleFormSubmit} encType="multipart/form-data">
                    <input
                      type="text"
                      name="name"
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="Your Email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                    <textarea
                      name="message"
                      placeholder="Your Message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                    ></textarea>

                    {/* File Attachment Input */}
                    <input
                      type="file"
                      name="attachment"
                      accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                      onChange={handleFileChange}
                    />

                    <SubmitButton type="submit">Send Message</SubmitButton>
                  </ContactForm>
                  {emailStatus && (
                    <StatusMessage isSuccess={emailStatus === 'Email sent successfully!'}>
                      {emailStatus}
                    </StatusMessage>
                  )}
                </ContactSection>
              )}
              {selectedCategory === 'docs' && (
                <DocsSection>
                <h3>Documentation</h3>
                <p>
                  Welcome to the Balanced Scorecard (BSC) System documentation. Here you will find all the resources you need to effectively use the platform, from setting up your account to advanced functionalities. Our documentation is divided into the following sections:
                </p>
                <ul>
                  <li><a href="#">Getting Started Guide</a></li>
                  <li><a href="#">API Documentation</a></li>
                  <li><a href="#">User Manual</a></li>
                </ul>
                
                <h4>Getting Started Guide</h4>
                <p>
                  The <a href="#">Getting Started Guide</a> is designed for new users. It covers the basics of setting up your account, navigating the platform, and configuring your first scorecard. This guide will help you get up and running quickly, providing step-by-step instructions with accompanying visuals.
                </p>
              
                <h4>API Documentation</h4>
                <p>
                  For developers and technical users, our <a href="#">API Documentation</a> provides a detailed reference for integrating the BSC system with other applications. This section includes information on authentication, endpoints, request and response formats, and example code snippets to help you make the most of our API.
                </p>
              
                <h4>User Manual</h4>
                <p>
                  The <a href="#">User Manual</a> is a comprehensive resource that covers all aspects of using the BSC platform. It includes detailed instructions on creating and managing scorecards, configuring KPIs, tracking performance, and utilizing advanced features such as approvals, reports, and analytics.
                </p>
              </DocsSection>
              
              )}
            </>
          )}
        </MainContent>
      </Content>
    </Container>
  );
};

export default HelpAndSupport;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  animation: ${fadeIn} 0.5s ease-in;
`;

const Content = styled.div`
  display: flex;
  flex: 1;
`;

const Sidebar = styled.div`
  width: 250px;
  background-color: #f8f9fa;
  padding: 1rem;
`;

const CategoryButton = styled.button<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.5rem;
  margin-bottom: 0.5rem;
  border: none;
  background-color: ${props => props.isActive ? '#01A8CE' : 'transparent'};
  color: ${props => props.isActive ? '#fff' : '#333'};
  cursor: pointer;
  transition: background-color 0.3s, color 0.3s;

  &:hover {
    background-color: ${props => props.isActive ? '#01A8CE' : '#e9ecef'};
  }

  .MuiSvgIcon-root {
    margin-right: 0.5rem;
  }
`;

const MainContent = styled.div`
  flex: 1;
  padding: 1rem;
  overflow-y: auto;
`;

const SearchBar = styled.input`
  width: 90%;
  padding: 0.5rem;
  margin-bottom: 1rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
`;

const FAQSection = styled.div`
  animation: ${fadeIn} 0.5s ease-in;
`;

const FAQCategory = styled.div`
  margin-bottom: 20px;
`;

const FAQCategoryTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: bold;
  margin-bottom: 10px;
`;

const FAQItem = styled.div`
  margin-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 1rem;
`;

const FAQQuestion = styled.h3`
  margin: 0 0 0.5rem 0;
  color: #01A8CE;
`;

const FAQAnswer = styled.p`
  margin: 0;
`;

const ContactSection = styled.div`
  animation: ${fadeIn} 0.5s ease-in;
`;

const ContactMethod = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;

  .MuiSvgIcon-root {
    margin-right: 0.5rem;
  }
`;

const ContactForm = styled.form`
  display: flex;
  flex-direction: column;

  input, textarea {
    margin-bottom: 1rem;
    padding: 0.5rem;
    border: 1px solid #ced4da;
    border-radius: 4px;
  }

  textarea {
    height: 100px;
  }
`;

const SubmitButton = styled.button`
  padding: 0.5rem 1rem;
  background-color: #01A8CE;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    background-color: #0193b7;
  }
`;

const StatusMessage = styled.p<{ isSuccess: boolean }>`
  margin-top: 1rem;
  font-size: 1rem;
  color: ${props => props.isSuccess ? '#28a745' : '#dc3545'};  // Green for success, red for error
`;

const DocsSection = styled.div`
  animation: ${fadeIn} 0.5s ease-in;

  ul {
    padding-left: 1rem;
  }

  a {
    color: #01A8CE;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const LoadingSpinner = styled.div`
  border: 4px solid #f3f3f3;
  border-top: 4px solid #01A8CE;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: ${spin} 1s linear infinite;
  margin: 2rem auto;
`;
